import { AxiosResponse } from 'axios'
import { captureException } from '@sentry/react'
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { FeatureFlagRow } from '../../../generated/api/features'
import {
  applyFeatureFlagRow,
  featureQueries,
  switchFeatureFlag,
} from '../../../api/features'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'

export const useSwitchFeatureFlag = (): UseMutationResult<
  AxiosResponse<{ data: FeatureFlagRow }>,
  Error,
  Parameters<typeof switchFeatureFlag>[0]
> => {
  const client = useQueryClient()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  return useMutation({
    mutationFn: switchFeatureFlag,
    // the answer is the flag's row, which replaces it in the list
    meta: { skipGlobalInvalidate: true },
    onSuccess: async (response) => {
      applyFeatureFlagRow(client, response.data.data)
      // a global switch sends no event, so the admin's own app refreshes its flags here
      await client.invalidateQueries({
        queryKey: featureQueries.getFeatures().queryKey,
      })
    },
    onError: (error) => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
      captureException(error)
    },
  })
}
